import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import { DollarSign, TrendingUp, TrendingDown, Repeat, Users, Activity } from 'lucide-react';

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FinancialDashboard({ api }) {
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [custoMensal, setCustoMensal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/stats/financial?months=6').then((r) => r.data).catch(() => null),
      api.get('/stats/revenue-forecast').then((r) => r.data).catch(() => null),
      api.get('/customer-plans/custo-mensal').then((r) => r.data).catch(() => null),
    ]).then(([fin, fc, custo]) => {
      if (!alive) return;
      setData(fin); setForecast(fc); setCustoMensal(custo);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  if (loading) {
    return (
      <Card><CardContent className="p-6 text-sm text-muted-foreground text-center">Carregando financeiro…</CardContent></Card>
    );
  }
  if (!data) return null;

  const months = data.months || [];
  const maxRev = Math.max(1, ...months.map((m) => m.revenue));
  const up = data.variacao_pct != null && data.variacao_pct >= 0;

  const kpi = (icon, label, value, sub, color) => (
    <div className={`rounded-2xl p-4 border bg-gradient-to-br ${color}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold opacity-90">
        {icon} {label}
      </div>
      <p className="text-2xl font-bold mt-1.5">{value}</p>
      {sub != null && <p className="text-[11px] mt-0.5 opacity-80">{sub}</p>}
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-500" />
          Financeiro
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {kpi(<Repeat className="w-3 h-3" />, 'MRR (receita recorrente)', brl(data.mrr),
            `${data.active_customers} clientes ativos`, 'from-emerald-500/15 to-emerald-600/5 border-emerald-500/20 text-emerald-400')}
          {kpi(<Activity className="w-3 h-3" />, 'Recebido este mês', brl(data.receita_mes_atual),
            data.variacao_pct != null
              ? <span className={up ? 'text-emerald-400' : 'text-red-400'}>{up ? '▲' : '▼'} {Math.abs(data.variacao_pct)}% vs mês anterior</span>
              : 'sem comparação', 'from-blue-500/15 to-blue-600/5 border-blue-500/20 text-blue-400')}
          {kpi(<TrendingDown className="w-3 h-3" />, 'Churn (30 dias)', `${data.churn_rate}%`,
            `${data.churn_count} não renovaram`, 'from-red-500/15 to-red-600/5 border-red-500/20 text-red-400')}
          {kpi(<Users className="w-3 h-3" />, 'Ticket médio', brl(data.ticket_medio),
            `LTV ${brl(data.ltv)}`, 'from-amber-500/15 to-amber-600/5 border-amber-500/20 text-amber-400')}
          {custoMensal?.tem_custo_configurado && kpi(<TrendingDown className="w-3 h-3" />, 'Meu custo mensal', brl(custoMensal.total || 0),
            `${custoMensal.detalhes?.reduce((a, d) => a + d.clientes, 0) || 0} cliente(s) · lucro ~${brl((data.mrr || 0) - (custoMensal.total || 0))}`, 'from-orange-500/15 to-orange-600/5 border-orange-500/20 text-orange-400')}
        </div>

        {/* Previsão de faturamento */}
        {forecast && (
          <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 to-primary/[0.02] p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                <TrendingUp className="w-4 h-4" /> Previsão para {forecast.mes}
              </div>
              {forecast.variacao_pct != null && (
                <span className={`text-xs font-medium ${forecast.variacao_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {forecast.variacao_pct >= 0 ? '▲' : '▼'} {Math.abs(forecast.variacao_pct)}% vs mês anterior
                </span>
              )}
            </div>
            <p className="text-3xl font-bold">{brl(forecast.previsao_total)}</p>
            {/* barra recebido vs a receber */}
            <div className="mt-3 h-2.5 rounded-full bg-muted/40 overflow-hidden flex">
              <div className="h-full bg-emerald-500" style={{ width: `${forecast.previsao_total > 0 ? (forecast.recebido / forecast.previsao_total) * 100 : 0}%` }} />
              <div className="h-full bg-primary/60" style={{ width: `${forecast.previsao_total > 0 ? (forecast.projecao_pendente / forecast.previsao_total) * 100 : 0}%` }} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Recebido: <b className="text-foreground">{brl(forecast.recebido)}</b></span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-primary/60" /> A receber: <b className="text-foreground">{brl(forecast.projecao_pendente)}</b></span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Projeção de {forecast.a_receber_qtd} cliente(s) que vencem este mês ({brl(forecast.a_receber_valor)}),
              ajustada pela sua taxa de renovação de <b className="text-foreground">{forecast.taxa_renovacao}%</b>.
            </p>
          </div>
        )}

        {/* Faturamento por mês */}
        <div>
          <p className="text-xs text-muted-foreground mb-2">Faturamento dos últimos {months.length} meses</p>
          <div className="h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} margin={{ top: 8, right: 6, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border/40" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false}
                       stroke="currentColor" className="text-muted-foreground" />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={44}
                       stroke="currentColor" className="text-muted-foreground"
                       tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip
                  cursor={{ fill: 'rgba(16,185,129,0.06)' }}
                  contentStyle={{ background: '#0e1524', border: '1px solid #1b2637', borderRadius: 12, fontSize: 12 }}
                  labelStyle={{ color: '#e8eef7' }}
                  formatter={(v, n, p) => [`${brl(v)} · ${p.payload.count} renov.`, 'Faturamento']}
                />
                <Bar dataKey="revenue" barSize={30} radius={[6, 6, 0, 0]}>
                  {months.map((m, i) => (
                    <Cell key={i} fill="#10b981" fillOpacity={i === months.length - 1 ? 0.95 : 0.5} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
